import { Button, Divider } from '@mui/material';
import { useEffect, useRef } from 'react';
import { ComponentListModal, SimpleDialog } from 'src/components/modal';
import { lang } from 'src/utils';

import { PositionAccordion } from '../positionAccordion';
import { ScreensDrawer } from '../screensDrawer';
import { ScreenSectionsDrawer } from '../screenSectionsDrawer';
import { SectionDetailDrawer } from '../sectionDetailDrawer';
import { useScheduleComponentPageSidebar } from './hooks/ScheduleComponentPageSidebar.hook';
import {
  ScheduleComponentSidebarWrapper,
  SidebarHeader,
} from './ScheduleComponentPageSidebar.styled';

export const ScheduleComponentPageSidebar = () => {
  const {
    isEditingSchedule,
    scheduleName,
    daypartName,
    showScreensDrawer,
    layerIndex,
    screenIndex,
    showScreenSectionsDrawer,
    isCodConfirmationOpen,
    screens,
    showSectionDetailDrawer,
    screenSections,
    positionsGridLines,
    previewingPositionIndex,
    showComponentListModal,
    filterParameters,
    selectedSection,
    modalTitle,
    removingInvalidBackupsConfirmationModal,
    onCloseScreensDrawer,
    onScheduleScreenComponent,
    onCloseScreenSectionsDrawer,
    onDeleteScreenSection,
    onScheduleSectionComponent,
    onCloseSectionDetailDrawer,
    onUpdateScreensGridSectionsWhenSectionChanges,
    hadScheduledAllSections,
    hasComponentsWithoutStartDate,
    hasInvalidBackups,
    onPreviewContent,
    onUpdatePositionDuration,
    onCloseComponentModal,
    onSelectedComponent,
    onReScheduleComponent,
    onAddAdditionalComponent,
    addNewPosition,
    handleScheduleComponent,
    handleScheduleNameChange,
    validateScheduleName,
    handleCloseInvalidBackupsConfirmationModal,
    hasAnyScheduledScreenForPosition,
  } = useScheduleComponentPageSidebar();
  const positionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const hasScrolledOnce = useRef(false);
  useEffect(() => {
    if (hasScrolledOnce.current) return;
    const el = positionRefs.current[previewingPositionIndex];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      hasScrolledOnce.current = true;
    }
  }, [previewingPositionIndex]);

  return (
    <ScheduleComponentSidebarWrapper data-testid="schedule-component-sidebar">
      {!showScreensDrawer && (
        <>
          <SidebarHeader variant="h6">
            {!isEditingSchedule
              ? lang.scheduleComponent.contentScheduling
              : lang.scheduleComponent.editContentScheduling}
          </SidebarHeader>
          <Divider />
          <PositionAccordion
            scheduleName={scheduleName}
            daypartName={daypartName}
            positions={positionsGridLines}
            addNewPosition={addNewPosition}
            previewingPositionIndex={previewingPositionIndex}
            onPreviewContent={onPreviewContent}
            onUpdatePositionDuration={onUpdatePositionDuration}
            handleScheduleNameChange={handleScheduleNameChange}
            getExtraContent={(index) => (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleScheduleComponent(index)}>
                {hasAnyScheduledScreenForPosition(index)
                  ? lang.editSchedule
                  : lang.scheduleComponent.label}
              </Button>
            )}
            options={{ enabledScheduleName: isEditingSchedule }}
            validateScheduleName={validateScheduleName}
            positionRefs={positionRefs}
          />
        </>
      )}
      {showScreensDrawer && (
        <>
          <ScreensDrawer
            layerIndex={layerIndex}
            showScreensDrawer={showScreensDrawer}
            screens={screens}
            options={{
              showAlertBox: !isEditingSchedule,
            }}
            onCloseScreensDrawer={onCloseScreensDrawer}
            onScheduleScreenComponent={onScheduleScreenComponent}
            hadScheduledAllSections={hadScheduledAllSections}
            hasComponentsWithoutStartDate={hasComponentsWithoutStartDate}
            hasInvalidBackups={hasInvalidBackups}
          />
          <ScreenSectionsDrawer
            screenIndex={screenIndex}
            showScreenSectionsDrawer={showScreenSectionsDrawer}
            screenSections={screenSections}
            onCloseScreenSectionsDrawer={onCloseScreenSectionsDrawer}
            onDeleteScreenSection={onDeleteScreenSection}
            onScheduleSectionComponent={onScheduleSectionComponent}
            onUpdateScreensGridSectionsWhenSectionChanges={
              onUpdateScreensGridSectionsWhenSectionChanges
            }
          />
          <SectionDetailDrawer
            showSectionDetailDrawer={showSectionDetailDrawer}
            selectedSection={selectedSection}
            isCodConfirmationOpen={isCodConfirmationOpen}
            onCloseSectionDetailDrawer={onCloseSectionDetailDrawer}
            onUpdateScreensGridSectionsWhenSectionChanges={
              onUpdateScreensGridSectionsWhenSectionChanges
            }
            onSwapComponent={onReScheduleComponent}
            onAddAdditionalComponent={onAddAdditionalComponent}
            duration={positionsGridLines[layerIndex - 1]?.duration}
          />
        </>
      )}

      {showComponentListModal && (
        <ComponentListModal
          title={modalTitle}
          open={showComponentListModal}
          filters={filterParameters}
          onClose={onCloseComponentModal}
          onSelectedComponent={onSelectedComponent}
          suggestedComponentTypes={
            selectedSection.config?.suggestedComponentTypes
          }
        />
      )}

      {removingInvalidBackupsConfirmationModal.open && (
        <SimpleDialog
          open={removingInvalidBackupsConfirmationModal.open}
          title={lang.confirmationRemovingInvalidBackupsModal.title}
          subTitle={lang.confirmationRemovingInvalidBackupsModal.subTitle}
          onClose={handleCloseInvalidBackupsConfirmationModal}
          onClickCancel={handleCloseInvalidBackupsConfirmationModal}
          modalSize="sm"
          onClickConfirm={removingInvalidBackupsConfirmationModal.customFn}
          confirmText={lang.confirmationRemovingInvalidBackupsModal.confirmText}
        />
      )}
    </ScheduleComponentSidebarWrapper>
  );
};
